import { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Typography, Box, Checkbox, CircularProgress } from "@material-ui/core";

import Filters from "./components/filters";
import RepresentativeCard from "./components/representative-card";

import useStructureQueries from "hooks/useStructureQueries";

import {
  updateRepresentative,
  updateRepresentativeFilters,
} from "redux/reducers/criarVitrineReducer";

export default function TargetAudience() {
  const [allRepresentatives, setAllRepresentatives] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { getRepresentatives } = useStructureQueries();

  const { formData, representatives = [], representativeFilters } = useSelector(
    (state) => state.criarVitrine
  );

  const idMarca = formData?.brand?.value;

  const handleGetRepresentatives = useCallback(async () => {
    if (!idMarca) {
      setAllRepresentatives([]);
      dispatch(updateRepresentative([]));
      return;
    }
    try {
      setIsLoading(true);
      const result = await getRepresentatives({
        idMarca,
      });
      const list = result?.data ?? [];
      const uniqueRepresentatives = list.filter(
        (rep, idx, arr) => arr.findIndex((item) => item.Id === rep.Id) === idx
      );
      setAllRepresentatives(uniqueRepresentatives);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, getRepresentatives, idMarca]);

  const filteredRepresentatives = useMemo(() => {
    return allRepresentatives.filter((representative) =>
      representative.Nome.toLowerCase().includes(
        representativeFilters.search.toLowerCase()
      )
    );
  }, [allRepresentatives, representativeFilters.search]);

  const selectedIds = useMemo(() => {
    return new Set(representatives.map((r) => r.Id));
  }, [representatives]);

  const handleCheckboxChange = useCallback(
    (id, checkAll) => {
      if (checkAll) {
        const areAllSelected =
          allRepresentatives.length > 0 &&
          representatives.length === allRepresentatives.length;

        if (areAllSelected) {
          dispatch(updateRepresentative([]));
        } else {
          dispatch(updateRepresentative(allRepresentatives));
        }
        return;
      }

      const representative = allRepresentatives.find((rep) => rep.Id === id);
      if (representative) {
        if (selectedIds.has(id)) {
          dispatch(
            updateRepresentative(representatives.filter((rep) => rep.Id !== id))
          );
        } else {
          dispatch(updateRepresentative([...representatives, representative]));
        }
      }
    },
    [allRepresentatives, representatives, selectedIds, dispatch]
  );

  const handleFilterChange = (key, value) => {
    dispatch(updateRepresentativeFilters({ [key]: value }));
  };

  useEffect(() => {
    handleGetRepresentatives();
  }, [handleGetRepresentatives]);

  return (
    <>
      <Filters
        isLoading={isLoading}
        filters={representativeFilters}
        setFilters={handleFilterChange}
      />
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        style={{ marginTop: "2rem" }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={
              allRepresentatives.length > 0 &&
              representatives.length === allRepresentatives.length
            }
            onChange={() => handleCheckboxChange(null, true)}
          />
          <Typography
            variant="h6"
            color="primary"
            style={{
              fontSize: "1.25rem",
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            Todos
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" width="100%">
          <Box maxHeight="500px" width="500px" style={{ overflowY: "scroll" }}>
            {filteredRepresentatives.map((representative, index) => (
              <RepresentativeCard
                key={index}
                representative={representative}
                checked={selectedIds.has(representative.Id)}
                onChange={() => handleCheckboxChange(representative.Id)}
              />
            ))}
            {isLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
